import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Check, Upload, Loader2, Sparkles, Calendar } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";
import { buildConsultationHref, getLastCaseStudyView } from "@/lib/cta";

/* ------------------------------ Step data ------------------------------ */
const PROJECT_TYPES = [
  { key: "crm", label: "CRM / Business Software" },
  { key: "mobile", label: "Mobile Application" },
  { key: "ai", label: "AI Solution" },
  { key: "web", label: "Web Application" },
  { key: "upgrade", label: "Upgrade Existing Software" },
  { key: "team", label: "Dedicated Development Team" },
  { key: "unsure", label: "Not Sure Yet" },
];
const GOALS = [
  "Automate manual processes", "Replace spreadsheets", "Replace existing software",
  "Launch a new digital product", "Introduce AI", "Integrate existing systems",
  "Improve customer experience", "Other",
];
const CAPABILITIES = [
  "User accounts", "Admin dashboard", "Mobile application", "Payments", "AI",
  "WhatsApp integration", "Email automation", "Document management",
  "Reporting/analytics", "Third-party API integrations", "Cloud infrastructure", "Not sure",
];
const STAGES = [
  "Idea", "Requirements prepared", "UI/UX prepared", "Existing software",
  "Already under development", "Need consultation first",
];
const TIMELINES = ["ASAP", "Within one month", "1–3 months", "3–6 months", "Researching / no fixed timeline"];
const BUDGET_BY_CURRENCY = {
  GBP: [{ v: "Under £5,000" }, { v: "£5,000–£10,000" }, { v: "£10,000–£25,000" }, { v: "£25,000–£50,000" }, { v: "£50,000+" }, { v: "Not sure yet" }],
  USD: [{ v: "Under $5,000" }, { v: "$5,000–$10,000" }, { v: "$10,000–$25,000" }, { v: "$25,000–$50,000" }, { v: "$50,000+" }, { v: "Not sure yet" }],
  EUR: [{ v: "Under €5,000" }, { v: "€5,000–€10,000" }, { v: "€10,000–€25,000" }, { v: "€25,000–€50,000" }, { v: "€50,000+" }, { v: "Not sure yet" }],
  PKR: [{ v: "Under Rs 1,000,000" }, { v: "Rs 1M–3M" }, { v: "Rs 3M–7M" }, { v: "Rs 7M–15M" }, { v: "Rs 15M+" }, { v: "Not sure yet" }],
};
const CURRENCIES = Object.keys(BUDGET_BY_CURRENCY);

/* Auto-pick currency from browser locale on first open */
function detectCurrency() {
  try {
    const region = (Intl.DateTimeFormat().resolvedOptions().locale || "").split("-")[1] || "";
    if (region === "GB") return "GBP";
    if (region === "US") return "USD";
    if (region === "PK") return "PKR";
    if (["DE", "FR", "IT", "ES", "NL", "IE", "AT", "BE", "FI", "PT"].includes(region)) return "EUR";
  } catch { /* ignore */ }
  return "GBP";
}

const STEPS = ["type", "goals", "capabilities", "stage", "budget", "timeline", "contact"];

/* Global event listener — pages/CTA buttons call:
   window.dispatchEvent(new CustomEvent("open-estimate-wizard", { detail: { presetType?, presetService? } })) */
export default function ProjectEstimateWizard() {
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Phase 3.3 source context — captured when the CTA opens the wizard.
  const [ctx, setCtx] = useState({});
  const [f, setF] = useState({
    project_type: null,
    goals: [],
    capabilities: [],
    stage: null,
    budget_range: null,
    currency: detectCurrency(),
    timeline: null,
    name: "", company: "", email: "", phone: "", country: "", description: "",
    brief_storage_path: null, brief_filename: null,
  });
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const openIt = (e) => {
      const detail = e.detail || {};
      const nextCtx = {
        serviceSlug: detail.presetService || null,
        serviceName: detail.presetServiceName || null,
        caseStudySlug: detail.caseStudySlug || null,
        caseStudyName: detail.caseStudyName || null,
        marketSlug: detail.marketSlug || null,
        sourcePage: detail.sourcePage || (typeof window !== "undefined" ? window.location.pathname : null),
        sourceType: detail.sourceType || detail.source || "cta",
        ctaLocation: detail.ctaLocation || null,
      };
      setCtx(nextCtx);
      setF((prev) => ({
        ...prev,
        project_type: detail.presetType || prev.project_type,
      }));
      setStepIdx(detail.presetType ? 1 : 0);
      setDone(false);
      setOpen(true);
      trackEvent("project_estimate_started", {
        preset_type: detail.presetType || null,
        source: nextCtx.sourceType,
        source_page: nextCtx.sourcePage || undefined,
        source_type: nextCtx.sourceType || undefined,
        service_slug: nextCtx.serviceSlug || undefined,
        case_study_slug: nextCtx.caseStudySlug || undefined,
        market_slug: nextCtx.marketSlug || undefined,
        cta_location: nextCtx.ctaLocation || undefined,
      });
    };
    window.addEventListener("open-estimate-wizard", openIt);
    return () => window.removeEventListener("open-estimate-wizard", openIt);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const currentStep = STEPS[stepIdx];
  const totalSteps = STEPS.length;
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case "type": return !!f.project_type;
      case "goals": return f.goals.length > 0;
      case "capabilities": return f.capabilities.length > 0;
      case "stage": return !!f.stage;
      case "budget": return !!f.budget_range;
      case "timeline": return !!f.timeline;
      case "contact": return f.name.trim() && /\S+@\S+\.\S+/.test(f.email);
      default: return false;
    }
  }, [currentStep, f]);

  const next = () => {
    if (!canProceed) return;
    trackEvent("project_estimate_step_completed", {
      step: currentStep,
      step_index: stepIdx,
      source_type: ctx.sourceType || undefined,
      service_slug: ctx.serviceSlug || undefined,
      case_study_slug: ctx.caseStudySlug || undefined,
    });
    if (stepIdx < totalSteps - 1) setStepIdx(stepIdx + 1);
  };
  const back = () => { if (stepIdx > 0) setStepIdx(stepIdx - 1); };

  const toggleFrom = (key, val) => {
    setF((s) => {
      const arr = s[key];
      return { ...s, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const onPickFile = () => fileInput.current?.click();
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File is larger than 15 MB — please compress or send by email.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/estimates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, brief_storage_path: res.data.storage_path, brief_filename: res.data.filename }));
      trackEvent("file_uploaded", { context: "project_estimate", size: file.size });
      toast.success("Brief attached.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const lastCs = getLastCaseStudyView();
      const directCsSlug = ctx.caseStudySlug || null;
      const assistedCsSlug =
        lastCs?.slug && lastCs.slug !== directCsSlug ? lastCs.slug : null;
      const assistedByCaseStudy = Boolean(directCsSlug || assistedCsSlug);
      // Extend attribution with source context — reuses the existing
      // attribution mechanism, no backend contract change.
      const attribution = {
        ...getAttribution(),
        source_page: ctx.sourcePage || undefined,
        source_type: ctx.sourceType || undefined,
        service_slug: ctx.serviceSlug || undefined,
        case_study_slug: directCsSlug || undefined,
        market_slug: ctx.marketSlug || undefined,
        cta_location: ctx.ctaLocation || undefined,
        assisted_by_case_study: assistedByCaseStudy,
        assisted_case_study_slug: assistedCsSlug || undefined,
      };
      const payload = { ...f, attribution };
      await api.post("/estimates", payload);
      trackEvent("project_estimate_submitted", {
        project_type: f.project_type,
        budget: f.budget_range,
        timeline: f.timeline,
        country: f.country,
        source_page: ctx.sourcePage || undefined,
        source_type: ctx.sourceType || undefined,
        service_slug: ctx.serviceSlug || undefined,
        case_study_slug: directCsSlug || undefined,
        market_slug: ctx.marketSlug || undefined,
        cta_location: ctx.ctaLocation || undefined,
        assisted_by_case_study: assistedByCaseStudy,
        assisted_case_study_slug: assistedCsSlug || undefined,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    // Wait for the exit animation to finish before resetting so users don't see a flash
    setTimeout(() => {
      setStepIdx(0); setDone(false);
      setF({
        project_type: null, goals: [], capabilities: [], stage: null,
        budget_range: null, currency: detectCurrency(), timeline: null,
        name: "", company: "", email: "", phone: "", country: "", description: "",
        brief_storage_path: null, brief_filename: null,
      });
    }, 350);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-stretch justify-end"
          initial={{ backgroundColor: "rgba(0,0,0,0)" }}
          animate={{ backgroundColor: "rgba(0,0,0,0.55)" }}
          exit={{ backgroundColor: "rgba(0,0,0,0)" }}
          onClick={close}
          data-testid="estimate-wizard-overlay"
        >
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="w-full sm:w-[560px] lg:w-[640px] h-full bg-background text-foreground shadow-[-30px_0_80px_-20px_rgba(0,0,0,0.5)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="estimate-wizard"
          >
            {/* Header */}
            <div className="px-6 md:px-8 pt-6 pb-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary inline-flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> Free Project Assessment
                </div>
                <h2 className="font-heading font-bold text-xl md:text-2xl mt-1">
                  {done ? "Thank you." : "Let's scope your project."}
                </h2>
              </div>
              <button onClick={close} className="p-2 rounded-full hover:bg-foreground/10" data-testid="estimate-wizard-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Contextual banner — only when opened from a case study */}
            {!done && ctx.caseStudySlug && ctx.caseStudyName && (
              <div className="px-6 md:px-8 pt-3" data-testid="estimate-wizard-context-banner">
                <div className="rounded-2xl border border-primary/25 bg-primary/[0.05] px-4 py-3 text-sm">
                  <span className="text-muted-foreground">Discussing a project similar to </span>
                  <span className="font-medium text-foreground">{ctx.caseStudyName}</span>
                  <span className="text-muted-foreground"> — we&apos;ll tailor the assessment to your context.</span>
                </div>
              </div>
            )}

            {/* Progress */}
            {!done && (
              <div className="px-6 md:px-8 py-3">
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  <span>Step {stepIdx + 1} of {totalSteps}</span>
                  <span>{Math.round(((stepIdx + 1) / totalSteps) * 100)}%</span>
                </div>
                <div className="mt-2 h-1 rounded-full bg-foreground/10 overflow-hidden">
                  <motion.div
                    className="h-full bg-primary"
                    initial={false}
                    animate={{ width: `${((stepIdx + 1) / totalSteps) * 100}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
              {done ? <SuccessPanel ctx={ctx} /> : (
                <>
                  {currentStep === "type" && (
                    <StepChoiceList
                      title="What would you like to build?"
                      subtitle="Pick the closest match — we'll refine together on the call."
                      options={PROJECT_TYPES.map((p) => ({ v: p.key, label: p.label }))}
                      value={f.project_type}
                      onSelect={(v) => setF({ ...f, project_type: v })}
                      testidPrefix="est-type"
                    />
                  )}
                  {currentStep === "goals" && (
                    <StepMultiChoice
                      title="What are you trying to achieve?"
                      subtitle="Select any that apply."
                      options={GOALS}
                      values={f.goals}
                      onToggle={(v) => toggleFrom("goals", v)}
                      testidPrefix="est-goal"
                    />
                  )}
                  {currentStep === "capabilities" && (
                    <StepMultiChoice
                      title="Which capabilities might you need?"
                      subtitle="A shortlist helps us estimate scope. You can add more later."
                      options={CAPABILITIES}
                      values={f.capabilities}
                      onToggle={(v) => toggleFrom("capabilities", v)}
                      testidPrefix="est-cap"
                    />
                  )}
                  {currentStep === "stage" && (
                    <StepChoiceList
                      title="What stage is your project at?"
                      subtitle=""
                      options={STAGES.map((s) => ({ v: s, label: s }))}
                      value={f.stage}
                      onSelect={(v) => setF({ ...f, stage: v })}
                      testidPrefix="est-stage"
                    />
                  )}
                  {currentStep === "budget" && (
                    <StepBudget f={f} setF={setF} />
                  )}
                  {currentStep === "timeline" && (
                    <StepChoiceList
                      title="What's your ideal timeline?"
                      subtitle=""
                      options={TIMELINES.map((t) => ({ v: t, label: t }))}
                      value={f.timeline}
                      onSelect={(v) => setF({ ...f, timeline: v })}
                      testidPrefix="est-timeline"
                    />
                  )}
                  {currentStep === "contact" && (
                    <StepContact
                      f={f} setF={setF}
                      onPickFile={onPickFile}
                      uploading={uploading}
                      fileInput={fileInput}
                      onFileChange={onFileChange}
                    />
                  )}
                </>
              )}
            </div>

            {/* Footer nav */}
            {!done && (
              <div className="px-6 md:px-8 py-4 border-t border-border flex items-center justify-between bg-background">
                <button
                  onClick={back}
                  disabled={stepIdx === 0}
                  className="text-sm inline-flex items-center gap-2 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="estimate-wizard-back"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                {currentStep === "contact" ? (
                  <button
                    onClick={submit}
                    disabled={!canProceed || submitting}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
                    data-testid="estimate-wizard-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Get My Free Project Assessment
                  </button>
                ) : (
                  <button
                    onClick={next}
                    disabled={!canProceed}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
                    data-testid="estimate-wizard-next"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------------------- Step primitives -------------------- */
function StepChoiceList({ title, subtitle, options, value, onSelect, testidPrefix }) {
  return (
    <div className="space-y-3">
      <h3 className="font-heading font-bold text-lg">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground -mt-1">{subtitle}</p>}
      <div className="grid gap-2 pt-2">
        {options.map((o) => (
          <button
            key={o.v}
            onClick={() => onSelect(o.v)}
            data-testid={`${testidPrefix}-${String(o.v).toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            className={`text-left rounded-xl px-4 py-3 border transition-colors ${
              value === o.v
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border hover:border-primary/40 hover:bg-foreground/5"
            }`}
          >
            <span className="text-sm font-medium">{o.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepMultiChoice({ title, subtitle, options, values, onToggle, testidPrefix }) {
  return (
    <div className="space-y-3">
      <h3 className="font-heading font-bold text-lg">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground -mt-1">{subtitle}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
        {options.map((o) => {
          const active = values.includes(o);
          return (
            <button
              key={o}
              onClick={() => onToggle(o)}
              data-testid={`${testidPrefix}-${o.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              className={`text-left rounded-xl px-4 py-2.5 border text-sm transition-colors inline-flex items-center gap-2 ${
                active ? "border-primary bg-primary/10" : "border-border hover:border-primary/40 hover:bg-foreground/5"
              }`}
            >
              <span className={`w-4 h-4 rounded-md border ${active ? "bg-primary border-primary" : "border-border"} flex items-center justify-center`}>
                {active && <Check className="w-3 h-3 text-primary-foreground" />}
              </span>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepBudget({ f, setF }) {
  const rows = BUDGET_BY_CURRENCY[f.currency] || BUDGET_BY_CURRENCY.GBP;
  return (
    <div className="space-y-3">
      <h3 className="font-heading font-bold text-lg">Budget</h3>
      <p className="text-sm text-muted-foreground -mt-1">Ballpark only — helps us match the right team & scope.</p>
      <div className="flex gap-2 pt-2 flex-wrap">
        {CURRENCIES.map((c) => (
          <button
            key={c}
            onClick={() => setF((s) => ({ ...s, currency: c, budget_range: null }))}
            data-testid={`est-currency-${c.toLowerCase()}`}
            className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              f.currency === c ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="grid gap-2 pt-2">
        {rows.map((r) => (
          <button
            key={r.v}
            onClick={() => setF((s) => ({ ...s, budget_range: r.v }))}
            data-testid={`est-budget-${r.v.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            className={`text-left rounded-xl px-4 py-3 border transition-colors ${
              f.budget_range === r.v ? "border-primary bg-primary/10 text-foreground" : "border-border hover:border-primary/40 hover:bg-foreground/5"
            }`}
          >
            <span className="text-sm font-medium">{r.v}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepContact({ f, setF, onPickFile, uploading, fileInput, onFileChange }) {
  const inputCls = "w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm focus:border-primary/60 outline-none";
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-heading font-bold text-lg">Where should we send the assessment?</h3>
        <p className="text-sm text-muted-foreground mt-1">A senior engineer will review your requirement personally within 24 hours.</p>
      </div>
      <div className="grid gap-3">
        <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Full name *</label>
        <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="est-name" placeholder="Jane Doe" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Company</label>
          <input className={inputCls + " mt-2"} value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="est-company" placeholder="Acme Ltd" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Country</label>
          <input className={inputCls + " mt-2"} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="est-country" placeholder="United Kingdom" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Business email *</label>
          <input className={inputCls + " mt-2"} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="est-email" placeholder="jane@company.com" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Phone / WhatsApp</label>
          <input className={inputCls + " mt-2"} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="est-phone" placeholder="+44 …" />
        </div>
      </div>
      <div>
        <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Project description</label>
        <textarea rows={4} className={inputCls + " mt-2 resize-y"} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="est-description" placeholder="A few lines about the problem, users, or what success looks like." />
      </div>
      {/* Attach brief */}
      <div className="rounded-2xl border border-dashed border-border p-4">
        <input ref={fileInput} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.txt,.md" className="hidden" onChange={onFileChange} data-testid="est-brief-input" />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-medium inline-flex items-center gap-2">
              <Upload className="w-4 h-4 text-primary" /> Attach project brief (optional)
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">PDF / DOC / XLS / images · max 15 MB</div>
            {f.brief_filename && <div className="text-xs text-primary mt-1 truncate max-w-[240px]" data-testid="est-brief-filename">{f.brief_filename}</div>}
          </div>
          <button type="button" onClick={onPickFile} disabled={uploading} className="text-sm rounded-full px-4 py-2 border border-border hover:bg-foreground/10 inline-flex items-center gap-2" data-testid="est-brief-choose">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {f.brief_storage_path ? "Replace" : "Choose file"}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">
        By submitting you agree we may contact you about your enquiry. We won't use your details for marketing without consent.
      </p>
    </div>
  );
}

function SuccessPanel({ ctx = {} }) {
  const href = buildConsultationHref({
    serviceSlug: ctx.serviceSlug,
    serviceName: ctx.serviceName,
    caseStudySlug: ctx.caseStudySlug,
    caseStudyName: ctx.caseStudyName,
    marketSlug: ctx.marketSlug,
  });
  const onConsult = () => {
    trackEvent("book_consultation_clicked", {
      context: "estimate_success",
      source_type: ctx.sourceType || undefined,
      service_slug: ctx.serviceSlug || undefined,
      case_study_slug: ctx.caseStudySlug || undefined,
      market_slug: ctx.marketSlug || undefined,
    });
  };
  return (
    <div className="space-y-6" data-testid="est-success">
      <div className="w-14 h-14 rounded-full bg-primary/15 grid place-items-center">
        <Check className="w-7 h-7 text-primary" />
      </div>
      <div className="space-y-2">
        <h3 className="font-heading font-bold text-2xl">Thank you.</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Our solutions team will review your requirement and contact you with initial recommendations — typically within 24 hours.
        </p>
      </div>
      <div className="rounded-2xl border border-border p-4 bg-foreground/[0.02]">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Prefer to talk first?</div>
        <div className="font-heading font-semibold text-lg mt-1">Book a 15-Minute Consultation</div>
        <p className="text-sm text-muted-foreground mt-1">Send us a short note requesting a 15-minute call — we&apos;ll come back with a time that suits you.</p>
        <Link
          to={href}
          onClick={onConsult}
          data-testid="est-book-consultation"
          className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 bg-primary text-primary-foreground text-sm font-medium"
        >
          <Calendar className="w-4 h-4" /> Book a 15-Minute Consultation <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
